'use client';

import { useCallback, useEffect, useState } from 'react';

import {
  fetchPublicSession,
  hasAuthenticatedPublicSession,
  type PublicSession,
} from '@/lib/public-session';

export function usePublicSession() {
  const [session, setSession] = useState<PublicSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const nextSession = await fetchPublicSession();
      setSession(nextSession);
      return nextSession;
    } catch {
      setSession(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    void (async () => {
      setIsLoading(true);
      try {
        const nextSession = await fetchPublicSession();
        if (active) {
          setSession(nextSession);
        }
      } catch {
        if (active) {
          setSession(null);
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  return {
    session,
    user: session?.user ?? null,
    isAuthenticated: hasAuthenticatedPublicSession(session),
    isLoading,
    refresh,
  };
}
